'use client'

import { useState, useEffect } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { ChevronDown, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { getPreferredLocale, getCurrentLocale } from '@/lib/utils'

const languages = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
] as const

type LocaleCode = (typeof languages)[number]['code']

function FlagIcon({ code }: { code?: LocaleCode }) {
  const commonClasses = "block h-4 w-6 overflow-hidden rounded-[2px] border border-border shadow-sm"

  if (code === 'fr') {
    return (
      <span className={commonClasses} aria-hidden="true">
        <span className="grid h-full w-full grid-cols-3">
          <span className="bg-[#0055a4]" />
          <span className="bg-white" />
          <span className="bg-[#ef4135]" />
        </span>
      </span>
    )
  }

  if (code === 'es') {
    return (
      <span className={commonClasses} aria-hidden="true">
        <span className="grid h-full w-full grid-rows-[1fr_2fr_1fr]">
          <span className="bg-[#aa151b]" />
          <span className="relative bg-[#f1bf00]">
            <span className="absolute left-[5px] top-1/2 h-1.5 w-1 -translate-y-1/2 rounded-[1px] bg-[#aa151b]" />
          </span>
          <span className="bg-[#aa151b]" />
        </span>
      </span>
    )
  }

  return (
    <span className={commonClasses} aria-hidden="true">
      <span className="relative block h-full w-full overflow-hidden bg-[#012169]">
        <span className="absolute left-1/2 top-1/2 h-[3px] w-8 -translate-x-1/2 -translate-y-1/2 rotate-[32deg] bg-white" />
        <span className="absolute left-1/2 top-1/2 h-[3px] w-8 -translate-x-1/2 -translate-y-1/2 -rotate-[32deg] bg-white" />
        <span className="absolute left-1/2 top-1/2 h-[1.5px] w-8 -translate-x-1/2 -translate-y-1/2 rotate-[32deg] bg-[#c8102e]" />
        <span className="absolute left-1/2 top-1/2 h-[1.5px] w-8 -translate-x-1/2 -translate-y-1/2 -rotate-[32deg] bg-[#c8102e]" />
        <span className="absolute left-1/2 top-0 h-full w-[5px] -translate-x-1/2 bg-white" />
        <span className="absolute left-0 top-1/2 h-[5px] w-full -translate-y-1/2 bg-white" />
        <span className="absolute left-1/2 top-0 h-full w-[3px] -translate-x-1/2 bg-[#c8102e]" />
        <span className="absolute left-0 top-1/2 h-[3px] w-full -translate-y-1/2 bg-[#c8102e]" />
      </span>
    </span>
  )
}

export function LanguageSelector() {
  const [isOpen, setIsOpen] = useState(false)
  const [currentLocale, setCurrentLocale] = useState<'en' | 'es' | 'fr'>('fr')
  const [isClient, setIsClient] = useState(false)
  const router = useRouter()
  const pathname = usePathname()

  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    // Get current locale from pathname
    const pathLocale = getCurrentLocale(pathname)
    setCurrentLocale(pathLocale)
  }, [pathname])

  const handleLanguageChange = (langCode: string) => {
    if (langCode === currentLocale) return

    // Store preference in localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('preferred-locale', langCode)
    }

    // Remove current locale from pathname if it exists
    const pathWithoutLocale = pathname.startsWith('/en') || pathname.startsWith('/es') || pathname.startsWith('/fr')
      ? pathname.substring(3) // Remove '/en', '/es', or '/fr'
      : pathname

    // Construct new path with selected language
    const newPath = `/${langCode}${pathWithoutLocale}`

    router.push(newPath)
    setIsOpen(false)
  }

  const currentLanguage = languages.find(lang => lang.code === currentLocale)

  // Don't render on server side to avoid hydration issues
  if (!isClient) {
    // Use getCurrentLocale to determine the correct locale during SSR
    const serverSideLocale = getCurrentLocale(pathname)
    const serverSideLanguage = languages.find(lang => lang.code === serverSideLocale)

    return (
      <div className="relative">
        <Button
          variant="ghost"
          size="sm"
          className="flex items-center space-x-1 text-sm font-medium hover:bg-transparent"
        >
          <FlagIcon code={serverSideLanguage?.code} />
          <span className="hidden sm:inline">{serverSideLanguage?.name}</span>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </div>
    )
  }

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 text-sm font-medium hover:bg-transparent"
      >
        <FlagIcon code={currentLanguage?.code} />
        <span className="hidden sm:inline">{currentLanguage?.name}</span>
        <ChevronDown className="h-4 w-4" />
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-40 rounded-md border bg-background py-1 shadow-lg z-50">
          {languages.map((language) => (
            <button
              key={language.code}
              onClick={() => handleLanguageChange(language.code)}
              className="flex w-full items-center space-x-2 px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
            >
              <FlagIcon code={language.code} />
              <span>{language.name}</span>
              {language.code === currentLocale && (
                <Check className="ml-auto h-4 w-4" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
